import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiService } from "../services/api";
import type { ImportConfig, ImportState } from "../types/import";

export const useImportStore = create<ImportState>()(
  devtools(
    (set) => ({
      // Initial state
      currentFile: null,
      preview: null,
      importConfig: null,
      currentJob: null,
      importJobs: [],
      currentStep: "upload",
      loading: false,
      error: null,

      // Basic actions
      setCurrentFile: (file) => set({ currentFile: file }),
      setPreview: (preview) => set({ preview }),
      setImportConfig: (config) => set({ importConfig: config }),
      setCurrentJob: (job) => set({ currentJob: job }),
      setCurrentStep: (step) => set({ currentStep: step }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      // Async actions using ApiService
      previewFile: async (file: File) => {
        try {
          set({ loading: true, error: null });

          const preview = await apiService.previewImport(file);

          set({
            preview,
            currentFile: file,
            loading: false,
          });
        } catch (error) {
          console.error("Error previewing file:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Failed to preview file";
          set({
            error: errorMessage,
            loading: false,
          });
          throw error;
        }
      },

      previewFileClientSide: async (file: File) => {
        try {
          set({ loading: true, error: null });

          const preview = await apiService.previewImportClientSide(file);

          set({
            preview,
            currentFile: file,
            loading: false,
          });
        } catch (error) {
          console.error("Error previewing file client-side:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Failed to preview file";
          set({
            error: errorMessage,
            loading: false,
          });
          throw error;
        }
      },

      startImport: async (file: File, config: ImportConfig) => {
        try {
          set({ loading: true, error: null });

          const result = await apiService.startImport(file, config);

          // Fetch the created job details
          const job = await apiService.getImportJob(result.job_id);

          set({
            currentJob: job,
            importConfig: config,
            loading: false,
          });
        } catch (error) {
          console.error("Error starting import:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Failed to start import";
          set({
            error: errorMessage,
            loading: false,
          });
          throw error;
        }
      },

      fetchImportJobs: async () => {
        try {
          set({ loading: true, error: null });

          const response = await apiService.getImportJobs();

          set({
            importJobs: response.jobs,
            loading: false,
          });
        } catch (error) {
          console.error("Error fetching import jobs:", error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to fetch import jobs";
          set({
            error: errorMessage,
            loading: false,
          });
        }
      },

      fetchImportJob: async (jobId: string) => {
        try {
          const job = await apiService.getImportJob(jobId);
          set({ currentJob: job });
        } catch (error) {
          console.error("Error fetching import job:", error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to fetch import job";
          set({ error: errorMessage });
        }
      },

      resetImport: () =>
        set({
          currentFile: null,
          preview: null,
          importConfig: null,
          currentJob: null,
          currentStep: "upload",
          error: null,
        }),
    }),
    { name: "import-store" }
  )
);
